// ProfessionalAnimationSystem.js
// Enterprise-grade animation system for smooth transitions and micro-interactions
// Integrates with mobile/desktop/shared architecture for optimal performance

import { MobileDetectionService } from './MobileDetectionService.js';

/**
 * Professional animation system
 * Provides consistent, performant animations across desktop and mobile
 * Respects device capabilities and user preferences
 */
export class ProfessionalAnimationSystem {
    static _instance = null;
    
    /**
     * Singleton pattern for consistent animation management
     */
    static getInstance() {
        if (!ProfessionalAnimationSystem._instance) {
            ProfessionalAnimationSystem._instance = new ProfessionalAnimationSystem();
        }
        return ProfessionalAnimationSystem._instance;
    }
    
    constructor() {
        if (ProfessionalAnimationSystem._instance) {
            return ProfessionalAnimationSystem._instance;
        }
        
        this.activeAnimations = new Set();
        this.animationSettings = this._getOptimalAnimationSettings();
        this._injectAnimationCSS();
        
        ProfessionalAnimationSystem._instance = this;
    }
    
    /**
     * Get optimal animation settings based on device capabilities
     * @private
     */
    _getOptimalAnimationSettings() {
        const isMobile = MobileDetectionService.isMobile();
        const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        
        // Respect user accessibility preferences
        if (prefersReducedMotion) {
            return {
                duration: {
                    fast: 150,
                    normal: 200,
                    slow: 250
                },
                easing: 'ease',
                enableComplexAnimations: false
            };
        }
        
        // Optimize for device type
        if (isMobile) {
            return {
                duration: {
                    fast: 200,
                    normal: 300,
                    slow: 400
                },
                easing: 'cubic-bezier(0.4, 0, 0.2, 1)', // Material Design easing
                enableComplexAnimations: true
            };
        } else {
            return {
                duration: {
                    fast: 150,
                    normal: 250,
                    slow: 350
                },
                easing: 'cubic-bezier(0.25, 0.46, 0.45, 0.94)', // Professional desktop easing
                enableComplexAnimations: true
            };
        }
    }
    
    /**
     * Inject professional animation CSS classes
     * @private
     */
    _injectAnimationCSS() {
        if (document.getElementById('professional-animations-css')) {
            return; // Already injected
        }
        
        const { duration, easing } = this.animationSettings;
        
        const animationCSS = `
            <style id="professional-animations-css">
                /* Professional Animation System - Enterprise Grade */
                
                /* Scene Transition Animations */
                .scene-fade-in {
                    animation: sceneFadeIn ${duration.normal}ms ${easing} forwards;
                    opacity: 0;
                }
                
                .scene-fade-out {
                    animation: sceneFadeOut ${duration.normal}ms ${easing} forwards;
                    opacity: 1;
                }
                
                @keyframes sceneFadeIn {
                    from {
                        opacity: 0;
                        transform: translateY(20px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }
                
                @keyframes sceneFadeOut {
                    from {
                        opacity: 1;
                        transform: translateY(0);
                    }
                    to {
                        opacity: 0;
                        transform: translateY(-10px);
                    }
                }
                
                /* Button Micro-interactions */
                .btn-professional {
                    transition: all ${duration.fast}ms ${easing};
                    transform: translateY(0);
                }
                
                .btn-professional:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 8px 25px rgba(0, 212, 255, 0.3);
                }
                
                .btn-professional:active {
                    transform: translateY(0);
                    transition: all ${duration.fast / 2}ms ${easing};
                }
                
                /* Navigation Item Animations */
                .nav-item {
                    transition: all ${duration.fast}ms ${easing};
                    position: relative;
                    overflow: hidden;
                }
                
                .nav-item::before {
                    content: '';
                    position: absolute;
                    top: 0;
                    left: -100%;
                    width: 100%;
                    height: 100%;
                    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.1), transparent);
                    transition: left ${duration.normal}ms ${easing};
                }
                
                .nav-item:hover::before {
                    left: 100%;
                }
                
                .nav-item:hover {
                    background: rgba(255, 255, 255, 0.05);
                    transform: translateX(4px);
                }
                
                /* Card Hover Effects */
                .glass-card, .kpi-card, .chart-card, .game-card {
                    transition: all ${duration.normal}ms ${easing};
                }
                
                .glass-card:hover, .kpi-card:hover, .chart-card:hover, .game-card:hover {
                    transform: translateY(-4px);
                    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.2);
                }
                
                /* Loading Animations */
                .loading-shimmer {
                    background: linear-gradient(90deg, 
                        rgba(255, 255, 255, 0.1) 0%, 
                        rgba(255, 255, 255, 0.2) 50%, 
                        rgba(255, 255, 255, 0.1) 100%);
                    background-size: 200% 100%;
                    animation: shimmer 2s infinite linear;
                }
                
                @keyframes shimmer {
                    0% { background-position: -200% 0; }
                    100% { background-position: 200% 0; }
                }
                
                /* Progress Animations */
                .progress-fill {
                    transition: width ${duration.slow}ms ${easing};
                }
                
                /* Notification Animations */
                .notification-slide-in {
                    animation: notificationSlideIn ${duration.normal}ms ${easing} forwards;
                    transform: translateX(100%);
                }
                
                .notification-slide-out {
                    animation: notificationSlideOut ${duration.normal}ms ${easing} forwards;
                    transform: translateX(0);
                }
                
                @keyframes notificationSlideIn {
                    from { transform: translateX(100%); opacity: 0; }
                    to { transform: translateX(0); opacity: 1; }
                }
                
                @keyframes notificationSlideOut {
                    from { transform: translateX(0); opacity: 1; }
                    to { transform: translateX(100%); opacity: 0; }
                }
                
                /* Content Reveal Animations */
                .content-reveal {
                    animation: contentReveal ${duration.normal}ms ${easing} forwards;
                    opacity: 0;
                    transform: translateY(30px);
                }
                
                @keyframes contentReveal {
                    from {
                        opacity: 0;
                        transform: translateY(30px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }
                
                /* Stagger Animation for Lists */
                .stagger-item {
                    opacity: 0;
                    transform: translateY(20px);
                    animation: staggerReveal ${duration.normal}ms ${easing} forwards;
                }
                
                .stagger-item:nth-child(1) { animation-delay: 0ms; }
                .stagger-item:nth-child(2) { animation-delay: 100ms; }
                .stagger-item:nth-child(3) { animation-delay: 200ms; }
                .stagger-item:nth-child(4) { animation-delay: 300ms; }
                .stagger-item:nth-child(5) { animation-delay: 400ms; }
                
                @keyframes staggerReveal {
                    from {
                        opacity: 0;
                        transform: translateY(20px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }
                
                /* Focus and Accessibility */
                .focus-visible {
                    outline: 2px solid #00d4ff;
                    outline-offset: 2px;
                    transition: outline-offset ${duration.fast}ms ${easing};
                }
                
                /* Reduced Motion Support */
                @media (prefers-reduced-motion: reduce) {
                    * {
                        animation-duration: 0.01ms !important;
                        animation-iteration-count: 1 !important;
                        transition-duration: 0.01ms !important;
                    }
                }
            </style>
        `;
        
        document.head.insertAdjacentHTML('beforeend', animationCSS);
        console.log('🎨 Professional animation system CSS injected');
    }
    
    /**
     * Animate scene transition
     * @param {string} overlayId - DOM overlay ID
     * @param {string} direction - 'in' or 'out'
     * @returns {Promise} - Resolves when animation completes
     */
    animateSceneTransition(overlayId, direction = 'in') {
        return new Promise((resolve) => {
            const overlay = document.getElementById(overlayId);
            if (!overlay) {
                resolve();
                return;
            }
            
            const animationClass = direction === 'in' ? 'scene-fade-in' : 'scene-fade-out';
            const duration = this.animationSettings.duration.normal;
            
            overlay.classList.add(animationClass);
            
            setTimeout(() => {
                overlay.classList.remove(animationClass);
                resolve();
            }, duration);
        });
    }
    
    /**
     * Animate content reveal with stagger effect
     * @param {string} containerSelector - CSS selector for container
     * @param {string} itemSelector - CSS selector for items to stagger
     */
    animateStaggerReveal(containerSelector, itemSelector = '.stagger-item') {
        const container = document.querySelector(containerSelector);
        if (!container) return;
        
        const items = container.querySelectorAll(itemSelector);
        items.forEach((item, index) => {
            item.style.animationDelay = `${index * 100}ms`;
            item.classList.add('stagger-item');
        });
    }
    
    /**
     * Animate loading state
     * @param {Element} element - Element to animate
     * @param {boolean} isLoading - Whether to show or hide loading
     */
    animateLoading(element, isLoading) {
        if (!element) return;
        
        if (isLoading) {
            element.classList.add('loading-shimmer');
        } else {
            element.classList.remove('loading-shimmer');
        }
    }
    
    /**
     * Show notification with animation
     * @param {string} message - Notification message
     * @param {string} type - Notification type ('success', 'error', 'info')
     * @param {number} duration - How long to show (ms)
     */
    showNotification(message, type = 'info', duration = 3000) {
        const notification = document.createElement('div');
        notification.className = `notification notification-${type}`;
        notification.textContent = message;
        
        // Style the notification
        Object.assign(notification.style, {
            position: 'fixed',
            top: '20px',
            right: '20px',
            padding: '16px 24px',
            background: type === 'success' ? '#10b981' : type === 'error' ? '#ef4444' : '#00d4ff',
            color: 'white',
            borderRadius: '12px',
            fontWeight: '600',
            zIndex: '10000',
            maxWidth: '400px',
            boxShadow: '0 10px 30px rgba(0, 0, 0, 0.2)'
        });
        
        document.body.appendChild(notification);
        
        // Animate in
        requestAnimationFrame(() => {
            notification.classList.add('notification-slide-in');
        });
        
        // Auto-remove
        setTimeout(() => {
            notification.classList.add('notification-slide-out');
            setTimeout(() => {
                if (notification.parentNode) {
                    notification.parentNode.removeChild(notification);
                }
            }, this.animationSettings.duration.normal);
        }, duration);
    }
    
    /**
     * Animate progress bar
     * @param {Element} progressBar - Progress bar element
     * @param {number} percentage - Target percentage (0-100)
     */
    animateProgress(progressBar, percentage) {
        if (!progressBar) return;
        
        const fill = progressBar.querySelector('.progress-fill');
        if (!fill) return;
        
        // Animate to target percentage
        requestAnimationFrame(() => {
            fill.style.width = `${Math.max(0, Math.min(100, percentage))}%`;
        });
    }
    
    /**
     * Apply professional button styling
     * @param {Element} button - Button element
     */
    enhanceButton(button) {
        if (!button) return;
        
        button.classList.add('btn-professional');
        
        // Add ripple effect on click
        button.addEventListener('click', (e) => {
            const ripple = document.createElement('span');
            const rect = button.getBoundingClientRect();
            const size = Math.max(rect.width, rect.height);
            const x = e.clientX - rect.left - size / 2;
            const y = e.clientY - rect.top - size / 2;
            
            ripple.style.cssText = `
                position: absolute;
                width: ${size}px;
                height: ${size}px;
                left: ${x}px;
                top: ${y}px;
                background: rgba(255, 255, 255, 0.3);
                border-radius: 50%;
                transform: scale(0);
                animation: ripple ${this.animationSettings.duration.normal}ms ease-out;
                pointer-events: none;
            `;
            
            button.style.position = 'relative';
            button.style.overflow = 'hidden';
            button.appendChild(ripple);
            
            setTimeout(() => {
                if (ripple.parentNode) {
                    ripple.parentNode.removeChild(ripple);
                }
            }, this.animationSettings.duration.normal);
        });
    }
    
    /**
     * Get animation duration for specific speed
     * @param {string} speed - 'fast', 'normal', or 'slow'
     * @returns {number} - Duration in milliseconds
     */
    getDuration(speed = 'normal') {
        return this.animationSettings.duration[speed] || this.animationSettings.duration.normal;
    }
    
    /**
     * Check if complex animations are enabled
     * @returns {boolean} - Whether complex animations should be used
     */
    supportsComplexAnimations() {
        return this.animationSettings.enableComplexAnimations;
    }
}

// Add ripple animation CSS
const rippleCSS = `
    @keyframes ripple {
        to {
            transform: scale(4);
            opacity: 0;
        }
    }
`;

// Inject ripple CSS
if (!document.getElementById('ripple-animation-css')) {
    const style = document.createElement('style');
    style.id = 'ripple-animation-css';
    style.textContent = rippleCSS;
    document.head.appendChild(style);
}

// Export singleton instance
export const professionalAnimations = ProfessionalAnimationSystem.getInstance();
export default professionalAnimations;





